// ============================================================
// storage.js
// ------------------------------------------------------------
// ศูนย์กลางการอ่าน/เขียน/ล้าง localStorage ของทั้งระบบ
// เดิมโค้ดกระจายอยู่ใน CustomComponent.js + เรียก localStorage ตรง ๆ ตามหน้าจอ
// ย้ายมารวมที่นี่เพื่อให้รู้ว่า "ระบบเก็บอะไรไว้บ้าง" จากไฟล์เดียว
//
// สำคัญ: พฤติกรรมทุกอย่างเหมือนเดิมเป๊ะ (คีย์เดิม ค่าเดิม ลำดับเดิม)
// ============================================================

// คีย์ทั้งหมดที่ระบบเก็บใน localStorage
// - SESSION_KEYS = ข้อมูลผู้ใช้/โครงการ ที่ต้องล้างตอน logout
// - ip_address ตั้งใจไม่ล้าง เพราะเป็นค่าตั้งเครื่อง ไม่ผูกกับผู้ใช้
//
// NOTE เดิม user_id / picture_paper / replace_text / sync_date ไม่ได้ถูกล้างตอน logout
//      ทำให้ข้อมูลของผู้ใช้/โครงการก่อนหน้าค้างข้ามไปยัง session ถัดไป (เช่น ชื่อค่าใช้จ่ายผิดโครงการ)
export const SESSION_KEYS = [
  "token", // token สำหรับยิง API (รูปแบบ "token xxxxx")
  "username", // ชื่อผู้ใช้ที่ล็อกอิน
  "full_name", // ชื่อ-นามสกุลเต็ม (แสดงบน Navbar)
  "user_id", // id ผู้ใช้ — หน้า BookBank ใช้เช็คว่าใครกำลังทำยอดอยู่
  "user_code", // รหัสพนักงาน
  "user_type", // ประเภทผู้ใช้ (admin / user)
  "role_detail", // สิทธิ์ของผู้ใช้ (JSON array) ใช้ซ่อน/แสดงปุ่มในแต่ละหน้า
  "first_page", // เมนูแรกที่ผู้ใช้มีสิทธิ์เข้า ใช้ redirect หลังล็อกอิน
  "project_id", // โครงการที่เลือกอยู่ตอนนี้
  "project_number",
  "project_name",
  "project_address",
  "phone_number",
  "tax_number",
  "water_id", // id ของชุดตั้งค่าค่าน้ำของโครงการ
  "receipt_id", // id เอกสารที่กำลังเปิดดู (ใบเสร็จ)
  "room_id", // id เอกสารที่กำลังเปิดดู (ใบแจ้งหนี้รายห้อง)
  "letter_id", // id เอกสารที่กำลังเปิดดู (หนังสือแจ้ง)
  "picture_paper", // รูปหัวกระดาษเอกสารของโครงการ
  "photo", // รูปโปรไฟล์ (base64)
  "replace_text", // cache ชื่อค่าใช้จ่ายที่โครงการตั้งเอง (ผูกกับโครงการ ต้องไม่ข้ามผู้ใช้)
  "sync_date", // วันที่คำนวณยอดค้างรอบล่าสุด (ผูกกับโครงการ)
  "DATA_", // ข้อมูลเอกสารที่ส่งให้หน้าพิมพ์ (ชื่อ/บ้านเลขที่/ยอดเงินของลูกบ้าน) — เดิมค้างอยู่หลัง logout
];

// อ่านค่าจาก localStorage ทีละคีย์
// type = ชื่อคีย์ เช่น getStorage("token"), getStorage("project_id")
export function getStorage(type) {
  // role_detail เก็บเป็น JSON string ต้อง parse ก่อนใช้
  if (type === "role_detail") {
    try {
      // caller เรียก .find/.reduce — ต้องเป็น array เท่านั้น ไม่งั้นถือว่าไม่มีข้อมูล
      const role_detail = JSON.parse(localStorage.getItem("role_detail"));
      return Array.isArray(role_detail) ? role_detail : null;
    } catch (e) {
      return null;
    }
  }
  // photo ต้องเติม prefix data URI ให้ <img src> ใช้ได้ (ถ้ายังไม่มี)
  if (type === "photo") {
    const photo = localStorage.getItem("photo");
    if (!photo) return photo; // กัน null.includes(...) ระเบิด
    return photo.includes("base64") ? photo : "data:image/png;base64," + photo;
  }
  return localStorage.getItem(type);
}

// อ่านค่า JSON จาก localStorage แบบกันค่าเสีย — อ่านไม่ออก/ไม่ใช่ object/ไม่มีคีย์ = null (ถือว่าไม่มีข้อมูล)
export function readStorageJSON(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value !== null && typeof value === "object" ? value : null;
  } catch (e) {
    return null;
  }
}

// เขียนค่าลง localStorage ทีละคีย์ (object จะถูก stringify ให้อัตโนมัติ)
export function setStorage(type, value) {
  localStorage.setItem(type, typeof value === "object" && value !== null ? JSON.stringify(value) : value);
}

// เขียนหลายคีย์พร้อมกัน เช่น setStorageAll({ token: "...", username: "..." })
export function setStorageAll(data) {
  Object.entries(data || {}).forEach(([key, value]) => setStorage(key, value));
}

// ล้างข้อมูล session ทั้งหมด (ใช้ตอน logout / token หมดอายุ)
export function removeStorage() {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

// ออกจากระบบ: ล้าง session แล้วเด้งไปหน้า login
export const Logout = async () => {
  removeStorage();
  window.location.href = "/login";
};

// token ที่อ่านครั้งเดียวตอน import โมดูล
// (คงพฤติกรรมเดิมของ CustomComponent.js — หน้าจอส่วนใหญ่ import ตัวนี้ไปใช้เป็น header)
export const tokens = getStorage("token");
